/* PREVIEW A PAGE — how it is meant to look, before it goes to the builder.
 *
 * Step 6 of docs/sites-pictures-spec.md. Her ask, 2026-10-16: "I don't see a
 * preview". It draws `pageLayout` (utils/siteLayout.js) and decides nothing
 * itself: every word is hers, every missing picture is a labelled placeholder,
 * every colour is from Me → Brand or a plain neutral that says so.
 *
 * TWO LAYERS OF COLOUR, and they must not mix: this overlay's own bar is
 * Brill's (dark, like the deck preview), the page inside it is HERS. Nothing
 * inside the frame reads a Brill token.
 *
 * "EXAMPLE" IS ON IT, ALWAYS. The builder makes the real page and its fonts
 * and spacing will differ; a preview that looked final would be a promise.
 *
 * Desktop and phone, toggled. Below 700px there is only the phone — a desktop
 * page squeezed into a phone is neither — and the toggle is not offered.
 * Nothing here calls Brill and nothing writes: it is a viewer.
 */
import { useEffect, useState } from "react";
import { Image as ImageIcon, Monitor, Smartphone, X } from "lucide-react";
import { loadGoogleFont } from "../../utils/markdown";
import { pageLayout } from "../../utils/siteLayout";
import { SITE_STYLES } from "../../utils/siteStyles";

const PHONE_W = 390;
const DESKTOP_MAX = 1180;
const TWO_VIEWS_MIN = 700;

function useWindowWidth() {
  const [w, setW] = useState(() => (typeof window === "undefined" ? 1280 : window.innerWidth));
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return w;
}

/* ── The four looks ─────────────────────────────────────────────────────
   One table, so a style is a set of decisions and not a fork of the page.
   Every section below reads `d`; the banner draws differently per style,
   because the banner is where the four looks actually part ways.
   FIRST DRAFTS for Bold, Classic and Artistic (2026-10-23): "something to
   start from … then we can polish". The adjectives are siteStyles.js's. */
const DESIGNS = {
  /* light · airy · elegant · soft */
  light_feminine: {
    banner: "beside", radius: 4, btnRadius: 4, headWeight: 400, headTrack: "0",
    h1: [40, 62], h2: [32, 44], btnCase: "uppercase", aboutBg: "soft", ctaBg: "dark",
    quoteBox: "soft", cardBox: "line", tilt: 0, numbered: false, rule: false, stagger: false,
  },
  /* dark · confident · modern · high contrast */
  bold: {
    /* 700, not heavier: the font loader fetches 400 and 700 only. */
    banner: "dark", radius: 10, btnRadius: 10, headWeight: 700, headTrack: "-0.02em",
    h1: [42, 68], h2: [30, 42], btnCase: "none", aboutBg: "plain", ctaBg: "button",
    quoteBox: "soft", cardBox: "soft", tilt: 0, numbered: false, rule: false, stagger: false, highlight: true,
  },
  /* polished · structured · calm · square */
  classic: {
    banner: "over", radius: 0, btnRadius: 0, headWeight: 500, headTrack: "0",
    h1: [36, 54], h2: [28, 38], btnCase: "uppercase", aboutBg: "soft", ctaBg: "dark",
    quoteBox: "line", cardBox: "line", tilt: 0, numbered: true, rule: true, stagger: false,
  },
  /* expressive · textured · asymmetric · earthy */
  artistic: {
    banner: "offset", radius: 0, btnRadius: 999, headWeight: 400, headTrack: "-0.01em",
    h1: [48, 92], h2: [36, 56], btnCase: "none", aboutBg: "block", ctaBg: "block",
    quoteBox: "none", cardBox: "block", tilt: -3, numbered: false, rule: false, stagger: true,
  },
};
const designFor = (id) => DESIGNS[id] || DESIGNS.light_feminine;

/* ── The pieces of her page ─────────────────────────────────────────────── */

function Picture({ picture, look, d, ratio = "4 / 5", round = false, small = false }) {
  const { colours: c } = look;
  const shape = { width: "100%", aspectRatio: ratio, borderRadius: round ? "50%" : d.radius, overflow: "hidden" };
  if (picture?.url) return <img src={picture.url} alt={picture.label} style={{ ...shape, objectFit: "cover", display: "block" }} />;
  return (
    <div role="img" aria-label={`Placeholder: ${picture?.label || "a picture"}`} title={picture?.label}
      style={{ ...shape, boxSizing: "border-box", border: `1.5px dashed ${c.muted}`, background: c.soft, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, color: c.muted, textAlign: "center", padding: small ? 0 : 14 }}>
      <ImageIcon size={small ? 16 : 26} strokeWidth={1.5} />
      {!small && <span style={{ fontSize: 13, lineHeight: 1.4 }}>{picture?.label}</span>}
      {!small && <span style={{ fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase", opacity: 0.8 }}>Placeholder</span>}
    </div>
  );
}

/* `on`: what the button sits on — "light" (her main button), "dark" (her
   light colour on a dark band), "button" (a band already in her button
   colour, so the button turns to her background). */
function Button({ text, look, d, on = "light" }) {
  if (!text) return null;
  const { colours: c } = look;
  const fill = on === "dark" ? c.textOnDark : on === "button" ? c.bg : c.button;
  const ink = on === "dark" ? c.dark : on === "button" ? c.button : c.buttonText;
  const caps = d.btnCase === "uppercase";
  return (
    <span style={{ display: "inline-block", background: fill, color: ink, padding: d.btnRadius > 100 ? "15px 34px" : "14px 30px", borderRadius: d.btnRadius, fontSize: caps ? 14 : 15.5, letterSpacing: caps ? "0.06em" : 0, textTransform: d.btnCase, fontWeight: d.headWeight >= 700 ? 700 : 600 }}>
      {text}
    </span>
  );
}

/* Paragraphs as she wrote them: a blank line or a new line is a new paragraph. */
function Paras({ text, style }) {
  return String(text || "").split(/\n+/).map((p) => p.trim()).filter(Boolean)
    .map((p, i) => <p key={i} style={{ margin: "0 0 14px", ...style }}>{p}</p>);
}

/* The headline: her lead, and her second line set apart the style's way —
   italic, or (Bold) in her highlight colour. */
function Headline({ headline, d, color, highlight, size, font }) {
  const { lead, italic } = headline;
  if (!lead) return null;
  return (
    <h1 style={{ fontFamily: font, fontWeight: d.headWeight, letterSpacing: d.headTrack, color, fontSize: size, lineHeight: d.headWeight >= 700 ? 1.02 : 1.06, margin: "0 0 24px" }}>
      {lead}{italic && <><br />{d.highlight
        ? <span style={{ color: highlight }}>{italic}</span>
        : <em style={{ fontStyle: "italic" }}>{italic}</em>}</>}
    </h1>
  );
}

/* Classic sets a short rule under every heading — the structure showing. */
function H2({ d, c, base, center = false, style, children }) {
  return (
    <>
      <h2 style={{ ...base, textAlign: center ? "center" : "left", ...style }}>{children}</h2>
      {d.rule && <div aria-hidden style={{ width: 48, height: 2, background: c.accent, margin: center ? "-4px auto 30px" : "-4px 0 24px" }} />}
    </>
  );
}

/* Classic's photo behind the words. A WIDE photo fills the band. A PORTRAIT —
   usually her headshot, the fallback when the banner has no picture of its
   own — stretched across a 1040px band showed a strip of forehead (her read,
   2026-10-23), so on a desktop it stands at the right, full height, and the
   tint fades into it. On a phone the band is taller than wide, so a portrait
   fills it fine. Its own component so the shape it learns on load is its own
   state, never a hook in Banner, which returns early per style. */
function ClassicPhoto({ picture, phone }) {
  const [tall, setTall] = useState(false);
  const onLoad = (e) => setTall(e.currentTarget.naturalHeight > e.currentTarget.naturalWidth * 0.9);
  const side = tall && !phone;
  return (
    <img src={picture.url} alt={picture.label} onLoad={onLoad}
      style={{ position: "absolute", top: 0, bottom: 0, right: 0, height: "100%", width: side ? "46%" : "100%", objectFit: "cover", objectPosition: side ? "50% 20%" : "75% 25%" }} />
  );
}

function Banner({ block, look, d, phone, font, body }) {
  const { colours: c } = look;
  const size = phone ? d.h1[0] : d.h1[1];
  const inner = { maxWidth: 1040, margin: "0 auto", position: "relative" };
  const eyebrow = (color) => block.eyebrow && (
    <div style={{ fontSize: 12.5, letterSpacing: "0.22em", textTransform: "uppercase", color, marginBottom: 20, fontWeight: d.headWeight >= 700 ? 700 : 400 }}>{block.eyebrow}</div>
  );
  const sub = (color) => block.sub && <div style={{ maxWidth: 520 }}><Paras text={block.sub} style={{ ...body, color }} /></div>;

  /* CLASSIC — the words over a wide photo, on a tint so they always read. */
  if (d.banner === "over") {
    const pic = block.picture;
    return (
      <section style={{ position: "relative", overflow: "hidden", background: c.dark, minHeight: phone ? 460 : 560, display: "flex", alignItems: "center" }}>
        {pic?.url
          ? <ClassicPhoto picture={pic} phone={phone} />
          : (
            <div role="img" aria-label="Placeholder: a wide photo behind the words"
              style={{ position: "absolute", inset: phone ? 12 : 18, border: `1.5px dashed ${c.textOnDark}`, opacity: 0.5, display: "flex", alignItems: "flex-end", justifyContent: "flex-end", gap: 8, padding: 14, color: c.textOnDark, fontSize: 11.5, letterSpacing: "0.12em", textTransform: "uppercase" }}>
              <ImageIcon size={15} strokeWidth={1.5} /> Placeholder · a wide photo behind the words
            </div>
          )}
        <div aria-hidden style={{ position: "absolute", inset: 0, background: phone ? `${c.dark}C0` : `linear-gradient(90deg, ${c.dark}E6 0%, ${c.dark}B3 55%, ${c.dark}40 100%)` }} />
        <div style={{ ...inner, width: "100%", padding: phone ? "56px 22px" : "96px 64px", boxSizing: "border-box" }}>
          <div style={{ maxWidth: 640 }}>
            {eyebrow(c.textOnDark)}
            <Headline headline={block.headline} d={d} color={c.textOnDark} size={size} font={font} />
            <div aria-hidden style={{ width: 64, height: 2, background: c.accent, margin: "0 0 22px" }} />
            {sub(c.textOnDark)}
            {block.button && <div style={{ marginTop: 14 }}><Button text={block.button} look={look} d={d} on="dark" /></div>}
          </div>
        </div>
      </section>
    );
  }

  /* BOLD — a dark band, big confident type, the picture beside it. */
  if (d.banner === "dark") {
    return (
      <section style={{ background: c.dark, padding: phone ? "40px 22px 56px" : "64px 64px 96px" }}>
        <div style={{ ...inner, display: "grid", gridTemplateColumns: phone ? "1fr" : "1.2fr 0.8fr", gap: phone ? 34 : 64, alignItems: "center" }}>
          <div>
            {eyebrow(c.highlight)}
            <Headline headline={block.headline} d={d} color={c.textOnDark} highlight={c.highlight} size={size} font={font} />
            {sub(c.textOnDark)}
            {block.button && <div style={{ marginTop: 14 }}><Button text={block.button} look={look} d={d} /></div>}
          </div>
          <div style={{ maxWidth: phone ? 300 : 400, width: "100%", justifySelf: phone ? "center" : "end" }}>
            <Picture picture={block.picture} look={look} d={d} />
          </div>
        </div>
      </section>
    );
  }

  /* ARTISTIC — oversized words beside a picture set at an angle, over a block
     of her colour that lines up with neither. */
  if (d.banner === "offset") {
    return (
      <section style={{ padding: phone ? "36px 22px 64px" : "56px 64px 120px", position: "relative", overflow: "hidden" }}>
        <div aria-hidden style={{ position: "absolute", background: c.block, top: phone ? "auto" : 120, height: phone ? 300 : "auto", bottom: phone ? 30 : 60, right: 0, width: phone ? "70%" : "42%" }} />
        <div style={{ ...inner, display: "grid", gridTemplateColumns: phone ? "1fr" : "1.4fr 0.8fr", gap: phone ? 40 : 24, alignItems: "end" }}>
          <div style={{ position: "relative", zIndex: 1 }}>
            {eyebrow(c.muted)}
            <Headline headline={block.headline} d={d} color={c.heading} size={size} font={font} />
            <div style={{ marginLeft: phone ? 0 : 120 }}>
              {sub(c.text)}
              {block.button && <div style={{ marginTop: 14 }}><Button text={block.button} look={look} d={d} /></div>}
            </div>
          </div>
          <div style={{ maxWidth: phone ? 240 : 360, width: "100%", justifySelf: phone ? "end" : "start", marginRight: phone ? 12 : 0, transform: `rotate(${d.tilt}deg)`, boxShadow: "0 18px 40px rgba(0,0,0,0.18)" }}>
            <Picture picture={block.picture} look={look} d={d} />
          </div>
        </div>
      </section>
    );
  }

  /* LIGHT & FEMININE — the photo tall beside the words, soft circles. */
  return (
    <section style={{ padding: phone ? "40px 22px 56px" : "72px 64px 104px", position: "relative", overflow: "hidden" }}>
      {/* The style's soft circles — her accent, faint, on the PICTURE's side:
          at top left one sat behind her eyebrow line. */}
      <div aria-hidden style={{ position: "absolute", width: phone ? 220 : 520, height: phone ? 220 : 520, borderRadius: "50%", background: c.soft, top: phone ? -110 : -140, right: phone ? -120 : -160 }} />
      <div style={{ ...inner, display: "grid", gridTemplateColumns: phone ? "1fr" : "1.15fr 1fr", gap: phone ? 34 : 72, alignItems: "center" }}>
        <div>
          {eyebrow(c.muted)}
          <Headline headline={block.headline} d={d} color={c.heading} size={size} font={font} />
          {sub(c.muted)}
          {block.button && <div style={{ marginTop: 14 }}><Button text={block.button} look={look} d={d} /></div>}
        </div>
        <div style={{ position: "relative", maxWidth: phone ? 300 : 420, width: "100%", justifySelf: phone ? "center" : "end" }}>
          <div aria-hidden style={{ position: "absolute", inset: phone ? "-14px -14px auto auto" : "-26px -26px auto auto", width: "70%", aspectRatio: "1", borderRadius: "50%", border: `1px solid ${c.accent}` }} />
          <div style={{ position: "relative" }}><Picture picture={block.picture} look={look} d={d} /></div>
        </div>
      </div>
    </section>
  );
}

function Section({ block, look, d, phone }) {
  const { colours: c, fonts } = look;
  const font = `"${fonts.heading}", Georgia, serif`;
  const pad = phone ? "56px 22px" : "96px 64px";
  const inner = { maxWidth: 1040, margin: "0 auto", position: "relative" };
  const h2 = { fontFamily: font, fontWeight: d.headWeight, letterSpacing: d.headTrack, color: c.heading, fontSize: phone ? d.h2[0] : d.h2[1], lineHeight: 1.12, margin: "0 0 18px" };
  const hp = { d, c, base: h2 };
  const body = { fontSize: phone ? 16 : 17, lineHeight: 1.75, color: c.text };
  const sub = block.sub ? <Paras text={block.sub} style={{ ...body, color: c.muted }} /> : null;
  const band = (k) => ({ soft: c.soft, block: c.block, dark: c.dark, button: c.button }[k] || c.bg);
  const box = (k) => (k === "soft" ? { background: c.soft, borderRadius: d.radius }
    : k === "block" ? { background: c.block, borderRadius: d.radius }
      : k === "line" ? { border: `1px solid ${c.accent}`, borderRadius: d.radius, background: c.bg }
        : {});
  const cols = (n) => (phone ? "1fr" : `repeat(${Math.min(Math.max(n, 1), 3)}, 1fr)`);
  /* Artistic staggers a row of cards; nothing else moves them. */
  const nudge = (i) => (d.stagger && !phone && i % 2 ? { transform: "translateY(28px)" } : {});
  const field = Math.min(d.btnRadius, 10);

  switch (block.kind) {
    case "banner":
      return <Banner block={block} look={look} d={d} phone={phone} font={font} body={body} />;
    case "about": {
      const onBlock = d.aboutBg === "block";
      return (
        <section style={{ padding: pad, background: band(d.aboutBg) }}>
          <div style={{ ...inner, display: "grid", gridTemplateColumns: phone ? "1fr" : onBlock ? "1.25fr 0.75fr" : "0.85fr 1.15fr", gap: phone ? 30 : 72, alignItems: "center" }}>
            <div style={{ maxWidth: phone ? 280 : 400, width: "100%", justifySelf: "center", order: onBlock && !phone ? 2 : 0, transform: d.tilt ? `rotate(${-d.tilt}deg)` : "none" }}>
              <Picture picture={block.picture} look={look} d={d} />
            </div>
            <div>
              {block.heading && <H2 {...hp}>{block.heading}</H2>}
              {block.paras.map((p, i) => <Paras key={i} text={p} style={body} />)}
            </div>
          </div>
        </section>
      );
    }
    case "services":
      return (
        <section style={{ padding: pad }}>
          <div style={inner}>
            {block.heading && <H2 {...hp} center={!d.stagger} style={{ marginBottom: d.rule ? 18 : 40 }}>{block.heading}</H2>}
            <div style={{ display: "grid", gridTemplateColumns: cols(block.items.length), gap: 22, paddingBottom: d.stagger && !phone ? 28 : 0 }}>
              {block.items.map((it, i) => (
                <div key={i} style={{ ...box(d.cardBox), padding: phone ? "26px 22px" : "34px 28px", ...nudge(i) }}>
                  {d.numbered && <div style={{ fontFamily: font, fontSize: 30, color: c.accent, marginBottom: 10 }}>{String(i + 1).padStart(2, "0")}</div>}
                  {it.name && <div style={{ fontFamily: font, fontWeight: d.headWeight, fontSize: 24, color: c.heading, lineHeight: 1.2, marginBottom: 12 }}>{it.name}</div>}
                  {it.line && <Paras text={it.line} style={{ ...body, fontSize: 15.5 }} />}
                  {it.price && <div style={{ marginTop: 8, fontSize: 13, letterSpacing: "0.14em", textTransform: "uppercase", color: c.heading, fontWeight: 600 }}>{it.price}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    case "testimonials": {
      const bare = d.quoteBox === "none";
      return (
        <section style={{ padding: pad }}>
          <div style={inner}>
            {block.heading && <H2 {...hp} center style={{ marginBottom: d.rule ? 18 : 40 }}>{block.heading}</H2>}
            <div style={{ display: "grid", gridTemplateColumns: cols(block.quotes.length), gap: 22 }}>
              {block.quotes.map((q, i) => (
                <figure key={i} style={{ margin: 0, ...box(d.quoteBox), padding: bare ? "0 0 0 22px" : phone ? "26px 22px" : "34px 30px", borderLeft: bare ? `3px solid ${c.accent}` : undefined, display: "flex", flexDirection: "column" }}>
                  {!bare && <div aria-hidden style={{ fontFamily: font, fontSize: 60, lineHeight: 0.6, color: c.accent, height: 30 }}>“</div>}
                  {q.text && <blockquote style={{ margin: 0, fontFamily: font, fontStyle: d.headWeight >= 700 ? "normal" : "italic", fontWeight: d.headWeight >= 700 ? 600 : 400, fontSize: bare ? (phone ? 22 : 26) : phone ? 19 : 20, lineHeight: 1.45, color: c.heading, flex: 1 }}>{q.text}</blockquote>}
                  {q.who && (
                    <figcaption style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 22, fontSize: 13.5, color: c.muted }}>
                      <span style={{ width: 42, flexShrink: 0 }}><Picture picture={q.picture} look={look} d={d} ratio="1" round small /></span>
                      {q.who}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          </div>
        </section>
      );
    }
    case "faq":
      return (
        <section style={{ padding: pad }}>
          <div style={{ ...inner, maxWidth: 760 }}>
            {block.heading && <H2 {...hp} center style={{ marginBottom: d.rule ? 18 : 34 }}>{block.heading}</H2>}
            {block.qs.map((q, i) => (
              <div key={i} style={{ borderTop: `1px solid ${c.accent}`, padding: "22px 0" }}>
                {q.q && <div style={{ fontFamily: font, fontWeight: d.headWeight >= 700 ? 700 : 400, fontSize: phone ? 21 : 23, color: c.heading, marginBottom: 10 }}>{q.q}</div>}
                {q.a && <Paras text={q.a} style={{ ...body, margin: "0 0 8px" }} />}
              </div>
            ))}
          </div>
        </section>
      );
    case "freebie":
      return (
        <section style={{ padding: pad, background: band(d.aboutBg === "plain" ? "soft" : d.aboutBg) }}>
          <div style={{ ...inner, display: "grid", gridTemplateColumns: phone ? "1fr" : "0.7fr 1.3fr", gap: phone ? 28 : 64, alignItems: "center" }}>
            <div style={{ maxWidth: phone ? 200 : 300, width: "100%", justifySelf: "center", transform: d.tilt ? `rotate(${d.tilt}deg)` : "none" }}>
              <Picture picture={block.picture} look={look} d={d} ratio="3 / 4" />
            </div>
            <div>
              {block.heading && <H2 {...hp}>{block.heading}</H2>}
              {sub}
              {/* The form is the builder's, drawn so the spot reads as a form. */}
              <div style={{ display: "flex", flexDirection: phone ? "column" : "row", gap: 10, marginTop: 10 }}>
                <span style={{ flex: 1, minHeight: 46, border: `1px solid ${c.accent}`, borderRadius: field, background: c.bg, display: "flex", alignItems: "center", padding: "0 14px", fontSize: 14, color: c.muted }}>Email</span>
                {block.button && <Button text={block.button} look={look} d={d} />}
              </div>
            </div>
          </div>
        </section>
      );
    case "cta": {
      const ink = d.ctaBg === "block" ? c.heading : d.ctaBg === "button" ? c.buttonText : c.textOnDark;
      const big = d.stagger;
      return (
        <section style={{ padding: pad, background: band(d.ctaBg), textAlign: big ? "left" : "center" }}>
          <div style={{ ...inner, maxWidth: big ? 1040 : 720 }}>
            {block.heading && <h2 style={{ ...h2, color: ink, fontSize: big ? (phone ? 40 : 64) : h2.fontSize }}>{block.heading}</h2>}
            {block.sub && <Paras text={block.sub} style={{ ...body, color: ink, opacity: 0.88 }} />}
            {block.button && <div style={{ marginTop: 18 }}><Button text={block.button} look={look} d={d} on={d.ctaBg === "block" ? "light" : d.ctaBg === "button" ? "button" : "dark"} /></div>}
          </div>
        </section>
      );
    }
    case "contact":
      return (
        <section style={{ padding: pad }}>
          <div style={{ ...inner, maxWidth: 640, textAlign: "center" }}>
            {block.heading && <H2 {...hp} center>{block.heading}</H2>}
            {sub}
            <div style={{ display: "grid", gap: 10, marginTop: 18, textAlign: "left" }}>
              {["Name", "Email", "Message"].map((f) => (
                <span key={f} style={{ minHeight: f === "Message" ? 96 : 46, border: `1px solid ${c.accent}`, borderRadius: field, padding: "13px 14px", boxSizing: "border-box", fontSize: 14, color: c.muted }}>{f}</span>
              ))}
            </div>
          </div>
        </section>
      );
    default:
      return (
        <section style={{ padding: pad }}>
          <div style={block.picture
            ? { ...inner, display: "grid", gridTemplateColumns: phone ? "1fr" : "1.15fr 0.85fr", gap: phone ? 28 : 64, alignItems: "center" }
            : { ...inner, maxWidth: 760 }}>
            <div>
              {block.heading && <H2 {...hp}>{block.heading}</H2>}
              <Paras text={block.text} style={body} />
            </div>
            {block.picture && <div style={{ maxWidth: phone ? 300 : 420, width: "100%", justifySelf: "center" }}><Picture picture={block.picture} look={look} d={d} ratio="4 / 3" /></div>}
          </div>
        </section>
      );
  }
}

/** Her page, drawn. Exported for the test; the overlay below is the screen. */
export function SitePage({ layout, phone }) {
  const { look } = layout;
  const { colours: c, fonts } = look;
  const d = designFor(layout.drawnAs);
  useEffect(() => { loadGoogleFont(fonts.heading); loadGoogleFont(fonts.body); }, [fonts.heading, fonts.body]);
  const firstButton = layout.blocks.find((b) => b.button)?.button;
  /* Bold's header joins its dark banner — but only with a logo that reads on
     dark (Me → Brand's logo for dark backgrounds). Her usual logo is dark
     lettering and vanished there, so without one the header stays light. */
  const darkTop = d.banner === "dark" && layout.blocks[0]?.kind === "banner" && (!!layout.logoDark || !layout.logo);
  const logo = darkTop ? layout.logoDark || layout.logo : layout.logo;
  const ink = darkTop ? c.textOnDark : c.heading;
  return (
    <div style={{ background: c.bg, color: c.text, fontFamily: `"${fonts.body}", system-ui, sans-serif`, textAlign: "left" }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, padding: phone ? "18px 22px" : "24px 64px", position: "relative", zIndex: 1, background: darkTop ? c.dark : "transparent" }}>
        {logo
          ? <img src={logo} alt="Logo" style={{ maxHeight: phone ? 34 : 44, maxWidth: 180, display: "block" }} />
          : <span role="img" aria-label="Placeholder: logo" style={{ border: `1.5px dashed ${darkTop ? c.textOnDark : c.muted}`, color: darkTop ? c.textOnDark : c.muted, borderRadius: d.radius, padding: "8px 16px", fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase" }}>Logo</span>}
        {firstButton && !phone && (
          <span style={{ border: `1px solid ${ink}`, color: ink, borderRadius: d.btnRadius, padding: "9px 18px", fontSize: 12.5, letterSpacing: d.btnCase === "uppercase" ? "0.08em" : 0, textTransform: d.btnCase, fontWeight: 600 }}>{firstButton}</span>
        )}
      </header>
      {layout.blocks.map((b) => <Section key={b.id} block={b} look={look} d={d} phone={phone} />)}
      <footer style={{ padding: phone ? "28px 22px" : "34px 64px", borderTop: `1px solid ${c.accent}`, fontSize: 13, color: c.muted, textAlign: "center" }}>
        {layout.name || " "}
      </footer>
    </div>
  );
}

/**
 * @param page      the page record
 * @param sections  its sections, in order
 * @param skin      brandSkin(...).skin
 * @param identity  { businessName, ownerName, logoUrl, ownerPhotoUrl }
 */
export function SitePreview({ page, sections = [], skin = {}, identity = {}, onClose }) {
  const width = useWindowWidth();
  const twoViews = width >= TWO_VIEWS_MIN;
  const [view, setView] = useState("desktop");
  /* WHICH LOOK TO SHOW — the page's own to start, and any of the four to
     compare on the same words. View only: the page's style is set in its cog,
     and the notes line says so whenever the two differ. */
  const [show, setShow] = useState(page?.style || "light_feminine");
  const phone = !twoViews || view === "phone";
  const layout = pageLayout({ page, sections, skin, identity, show });

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const tab = (id, icon, label) => (
    <button key={id} type="button" onClick={() => setView(id)} aria-pressed={view === id}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, background: view === id ? "rgba(255,255,255,0.16)" : "none", border: "1px solid rgba(255,255,255,0.25)", color: "#fff", borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
      {icon} {label}
    </button>
  );

  const example = <span key="example" style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", border: "1px solid rgba(255,255,255,0.35)", borderRadius: 999, padding: "3px 10px", opacity: 0.85 }}>Example</span>;
  const styleSelect = (
    <select key="style" value={layout.drawnAs} onChange={(e) => setShow(e.target.value)} aria-label="Style to show"
      style={{ background: "#1b1b26", color: "#fff", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "6px 8px", fontSize: 12, fontFamily: "inherit", cursor: "pointer" }}>
      {SITE_STYLES.map((st) => <option key={st.id} value={st.id}>{st.label}{st.id === page?.style ? " (this page)" : ""}</option>)}
    </select>
  );

  return (
    <div role="dialog" aria-modal="true" aria-label={`Preview of ${page?.title || "this page"}`}
      style={{ position: "fixed", inset: 0, zIndex: 120, background: "#0b0b12", display: "flex", flexDirection: "column", color: "#fff" }}>
      {/* ON A PHONE, TWO ROWS: the page's name and the way out, then what she
          is looking at. In one row the pill and the style picker took the
          width and the name was cut to a few letters (her read, 2026-10-23). */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: twoViews ? "12px 16px" : "10px 12px 6px 16px", flexWrap: twoViews ? "wrap" : "nowrap" }}>
        <div style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {page?.title || "Untitled page"}
        </div>
        {twoViews && [example, styleSelect, tab("desktop", <Monitor size={14} />, "Desktop"), tab("phone", <Smartphone size={14} />, "Phone")]}
        <button type="button" onClick={onClose} aria-label="Close preview"
          style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.18)", color: "#fff", borderRadius: 999, width: 36, height: 36, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
          <X size={18} />
        </button>
      </div>
      {!twoViews && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 16px 8px" }}>
          {styleSelect}{example}
        </div>
      )}
      <div style={{ padding: "0 16px 10px", fontSize: 12.5, lineHeight: 1.55, color: "rgba(255,255,255,0.72)" }}>
        How this page is meant to look. Your builder makes the real one, so its fonts and spacing will differ.
        {layout.notes.map((n) => <div key={n}>· {n}</div>)}
      </div>
      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: twoViews ? "8px 16px 32px" : 0 }}>
        {layout.blocks.length ? (
          <div style={{ width: "100%", maxWidth: phone ? PHONE_W : DESKTOP_MAX, margin: "0 auto", borderRadius: twoViews ? (phone ? 22 : 8) : 0, overflow: "hidden", boxShadow: "0 10px 40px rgba(0,0,0,0.45)" }}>
            <SitePage layout={layout} phone={phone} />
          </div>
        ) : (
          <div style={{ textAlign: "center", fontSize: 14, opacity: 0.7, padding: 40 }}>Nothing on this page is written yet.</div>
        )}
      </div>
    </div>
  );
}
